import {
  AutoComplete,
  Checkbox,
  Form,
  Input,
  Modal,
  Spin,
} from "antd";
import {
  addCompanyAndTrader,
  getCompanySuggestions,
  getTradingCompanies,
} from "api/teamRunApi";

import {
  ADD_Company_MODAL_INFO,
  ADD_TRADE_MODAL_INFO,
} from "constants/teamRun";
import { GlobalContext } from "contexts/GlobalContext";
import {
  companyNamesMatch,
  companySuggestionPayload,
  existingCompanyOptionValue,
  extractCompanyLabelsFromTradingCompanies,
  filterCompanyNamesByQuery,
  hasExactCompanyMatch,
  mergeCompanySuggestionLists,
  normalizeCompanySuggestionNames,
  stripExistingCompanySuffix,
} from "helpers/TeamRun/companySuggestions";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  CancelButton,
  PrimaryButton,
} from "components/Common/CustomButtons/CustomButtons";
import { useQuery } from "@tanstack/react-query";
import { fetchProductsAPI } from "api/pricerApi";

const MIN_QUERY_LENGTH = 2;

function AddCompanyForm({
  isOpen,
  setIsOpen = () => {},
  setRefetchCompanies,
  productId,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [knownCompanyNames, setKnownCompanyNames] = useState([]);
  const [apiSuggestionNames, setApiSuggestionNames] = useState([]);
  const [selectedCommodity, setSelectedCommodity] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [traderName, setTraderName] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [nameValidated, setNameValidated] = useState(false);
  const [validationMessage, setValidationMessage] = useState(null);
  const [nameFocused, setNameFocused] = useState(false);

  const searchQuery = stripExistingCompanySuffix(companyName);

  const { data: commodityData, isLoading: commodityLoading } = useQuery(
    ["company/products"],
    async () => {
      const res = await fetchProductsAPI();
      return res?.data.data.map((item) => ({
        label: item.title,
        value: item.id,
      }));
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);

  useEffect(() => {
    if (!isOpen) return;
    setCompanyName("");
    setTraderName("");
    setSelectedCommodity([]);
    setApiSuggestionNames([]);
    setNameValidated(false);
    setValidationMessage(null);
    setIsChecking(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const requests = [getTradingCompanies({})];
        if (productId != null && productId !== "") {
          requests.push(getTradingCompanies({ product_id: productId }));
        }
        const results = await Promise.allSettled(requests);
        if (cancelled) return;
        const labelLists = results
          .filter((result) => result.status === "fulfilled")
          .map((result) =>
            extractCompanyLabelsFromTradingCompanies(result.value?.data?.data)
          );
        setKnownCompanyNames(mergeCompanySuggestionLists(...labelLists));
      } catch {
        if (!cancelled) setKnownCompanyNames([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, productId]);

  const localMatches = useMemo(
    () =>
      searchQuery.length >= MIN_QUERY_LENGTH
        ? filterCompanyNamesByQuery(knownCompanyNames, searchQuery)
        : [],
    [knownCompanyNames, searchQuery]
  );

  const suggestions = useMemo(
    () => mergeCompanySuggestionLists(localMatches, apiSuggestionNames),
    [localMatches, apiSuggestionNames]
  );

  useEffect(() => {
    if (searchQuery.length < MIN_QUERY_LENGTH) {
      setApiSuggestionNames([]);
      setNameValidated(false);
      setValidationMessage(null);
      setIsChecking(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsChecking(true);
      setNameValidated(false);
      try {
        const res = await getCompanySuggestions({ name: searchQuery });
        if (cancelled) return;

        const status = res?.status;
        const body = res?.data;
        const apiNames = normalizeCompanySuggestionNames(
          companySuggestionPayload(body)
        );
        setApiSuggestionNames(apiNames);

        const exactInList = hasExactCompanyMatch(
          searchQuery,
          mergeCompanySuggestionLists(knownCompanyNames, apiNames)
        );
        const isDuplicate =
          status === 422 ||
          exactInList ||
          companyNamesMatch(searchQuery, body?.name);

        if (isDuplicate) {
          setNameValidated(false);
          setValidationMessage(
            body?.message ?? "This trading company already exists."
          );
        } else if (status === 200) {
          setNameValidated(true);
          setValidationMessage(null);
        } else {
          setNameValidated(false);
          setValidationMessage(body?.message ?? null);
        }
      } catch {
        if (!cancelled) {
          setApiSuggestionNames([]);
          const exactLocal = hasExactCompanyMatch(searchQuery, knownCompanyNames);
          setNameValidated(!exactLocal && searchQuery.length >= MIN_QUERY_LENGTH);
          setValidationMessage(
            exactLocal ? "This trading company already exists." : null
          );
        }
      } finally {
        if (!cancelled) setIsChecking(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery, knownCompanyNames]);

  const handleSendTypeChange = (checkedValues) => {
    setSelectedCommodity(checkedValues);
  };

  const onFinish = async () => {
    setIsLoading(true);
    await addCompanyAndTrader({
      company_name: searchQuery,
      trader_name: traderName,
      products: selectedCommodity,
    })
      .then((_) => {
        setIsLoading(false);
        setRefetchCompanies(true);
        setIsOpen(false);
      })
      .catch((_) => {
        setIsLoading(false);
      });
  };

  const hideModal = () => {
    setIsOpen(false);
  };

  const suggestionOptions = useMemo(
    () =>
      suggestions.map((name) => ({
        value: existingCompanyOptionValue(name),
      })),
    [suggestions]
  );

  const dropdownOpen =
    nameFocused && suggestionOptions.length > 0 && !isLoading;

  const canSubmit =
    nameValidated &&
    !isChecking &&
    searchQuery.length >= MIN_QUERY_LENGTH &&
    traderName.trim().length > 0 &&
    selectedCommodity.length > 0 &&
    !hasExactCompanyMatch(searchQuery, knownCompanyNames);

  return (
    <div className="">
      <Modal
        open={isOpen}
        width={"40%"}
        centered
        className={dark_Theme && "ant-modal-dark content_dark"}
        onCancel={hideModal}
        title={ADD_Company_MODAL_INFO.ADD_TRADING_COMPANY.label}
        bodyStyle={{ height: "300px", overflow: "visible" }}
        footer={[
          <CancelButton disabled={isLoading} key="back" onClick={hideModal}>
            {ADD_TRADE_MODAL_INFO.CANCEL.label}
          </CancelButton>,

          <PrimaryButton
            onClick={onFinish}
            loading={isLoading}
            disabled={!canSubmit || isLoading}
            key="submit"
          >
            {ADD_TRADE_MODAL_INFO.FINISH.label}
          </PrimaryButton>,
        ]}
      >
        <Form
          name="basic"
          className="w-100 h-100 m-auto gap-3 d-flex flex-column justify-content-center align-items-center"
          autoComplete="off"
        >
          <div
            style={{
              color: dark_Theme
                ? "var(--color-white-1)"
                : "var(--color-white-1)",
              height: "20px",
            }}
            className="fw-bold w-100 text-center"
          >
            {isChecking && "Checking if the company already exists..."}
          </div>
          <div style={{ width: "100%", position: "relative" }}>
            <div className="w-100 d-flex justify-content-between align-items-center">
              <label className="fw-medium fs-6">Trading Company Name:</label>
              <AutoComplete
                allowClear
                disabled={isLoading}
                value={companyName}
                options={suggestionOptions}
                filterOption={false}
                open={dropdownOpen}
                style={{ width: "70%" }}
                dropdownClassName={
                  dark_Theme ? "dropdown-dark exist-warning" : "exist-warning"
                }
                dropdownStyle={{ zIndex: 1100 }}
                getPopupContainer={() => document.body}
                onFocus={() => setNameFocused(true)}
                onBlur={() => setNameFocused(false)}
                onSelect={(value) => {
                  setCompanyName(stripExistingCompanySuffix(value));
                }}
                onChange={(value) => {
                  setCompanyName(value ?? "");
                }}
                placeholder="Enter Company Name"
              />
            </div>
            {isChecking && (
              <div
                style={{
                  position: "absolute",
                  right: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <Spin size="small" />
              </div>
            )}
          </div>
          {validationMessage && searchQuery && (
            <div
              style={{ height: "20px", width: "70%" }}
              className="text-warning fw-bold text-center"
            >
              {validationMessage}
            </div>
          )}
          <div className="w-100 d-flex justify-content-between align-items-center">
            <label className="fw-medium fs-6">Trader Name:</label>

            <Input
              style={{ width: "70%" }}
              required
              disabled={isLoading}
              placeholder="Enter Trader Name"
              type="text"
              onChange={(e) => {
                setTraderName(e.target.value);
              }}
              value={traderName}
            />
          </div>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <label className="fw-medium fs-6">Commodities:</label>
            {commodityLoading ? (
              <Spin size="small" />
            ) : (
              <Checkbox.Group
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  width: "70%",
                  gap: "10px",
                  color: "white",
                }}
                options={commodityData}
                value={selectedCommodity}
                onChange={handleSendTypeChange}
              />
            )}
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default AddCompanyForm;
